/**
 * API calls for managing faculty (tutors).
 * Adapts the backend contract to frontend types.
 */

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type {
  BatchTutorsResponse,
  GradeOption,
  GroupOption,
  PaginationMeta,
  RawTutor,
  TutorsListResponse,
} from 'api/arcMosaico/types'
import { api } from 'helpers'
import type { ManagedFaculty } from '../types'

/**
 * Guard for 401 responses. The axios interceptor does NOT reject on 401
 * (it returns the response directly), so we must check status manually.
 */
function assertAuthenticated(response: { status: number }): void {
  if (response.status === 401) {
    window.location.href = '/error_permission'
    throw new Error('Unauthorized')
  }
}

// ============================================================================
// Adapter — backend shape → ManagedFaculty
// ============================================================================

function adaptTutor(raw: RawTutor): ManagedFaculty {
  let status: ManagedFaculty['status'] = 'active'
  if (raw.invitationStatus === 'invited' || raw.invitationStatus === 'pending') {
    status = 'pending_confirmation'
  }

  return {
    id: raw.id,
    firstName: raw.firstName,
    lastName: raw.lastName,
    email: raw.email,
    role: raw.educatorRole || 'teacher',
    avatarUrl: raw.avatarUrl,
    grades: raw.grades || [],
    groups: raw.groups || [],
    permissions: { editProfile: true, remove: true },
    status,
    createdAt: raw.createdAt || '',
  }
}

// ============================================================================
// Tutors
// ============================================================================

export interface ListTutorsParams {
  page?: number
  limit?: number
  search?: string
  enabled?: boolean
}

export async function listTutors(
  params: ListTutorsParams,
): Promise<{ tutors: ManagedFaculty[]; pagination: PaginationMeta }> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.tutors.list, {
    params: {
      page: params.page || 1,
      limit: params.limit || 9,
      search: params.search || undefined,
      enabled: params.enabled,
    },
  })
  assertAuthenticated(response)

  const data = response.data as TutorsListResponse
  return {
    tutors: (data.tutors || []).map(adaptTutor),
    pagination: data.pagination,
  }
}

export async function batchCreateTutors(
  tutors: Array<{
    firstName: string
    lastName: string
    email: string
    gradeIds?: string[]
    groupSchoolTermIds?: string[]
  }>,
): Promise<BatchTutorsResponse> {
  const response = await api.post(ARC_MOSAICO_ENDPOINTS.tutors.batch, { tutors })
  assertAuthenticated(response)
  return response.data as BatchTutorsResponse
}

export async function updateTutor(
  id: string,
  data: {
    firstName?: string
    lastName?: string
    educatorRole?: string
    gradeIds?: string[]
    groupSchoolTermIds?: string[]
  },
): Promise<void> {
  const response = await api.put(ARC_MOSAICO_ENDPOINTS.tutors.update(id), data)
  assertAuthenticated(response)
}

export async function deleteTutor(id: string): Promise<void> {
  const response = await api.delete(ARC_MOSAICO_ENDPOINTS.tutors.destroy(id))
  assertAuthenticated(response)
}

// ============================================================================
// Grades & Groups (reuse existing endpoints)
// ============================================================================

export async function listGrades(): Promise<GradeOption[]> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.grades.list)
  assertAuthenticated(response)
  return response.data as GradeOption[]
}

export async function listGroups(): Promise<{ groups: GroupOption[], courseId: string | null }> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.groups.list, {
    params: { limit: 100 },
  })
  assertAuthenticated(response)
  const data = response.data as { groups: Array<GroupOption & { course?: { id: string } }> }
  const groups = (data.groups || []).map(({ course: _c, ...g }) => g as GroupOption)
  const courseId = data.groups?.[0]?.course?.id ?? null
  return { groups, courseId }
}
